import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle 
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { getTimezone } from '../../lib/utils';
import { 
  Clock, 
  Package, 
  User, 
  Edit3, 
  Save, 
  AlertTriangle,
  Loader2,
  History
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function EditarRegisto({ 
  open, 
  onOpenChange, 
  registoId, 
  onSuccess 
}) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [registo, setRegisto] = useState(null);
  const [formData, setFormData] = useState({
    horaInicio: '',
    horaFim: '',
    quantidade: '',
    anomalias: '',
    motivoEdicao: ''
  });
  const [errors, setErrors] = useState({});

  // Carregar dados do registo
  useEffect(() => {
    if (open && registoId) {
      loadRegisto();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, registoId]);

  const loadRegisto = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/registos/${registoId}`);
      const data = res.data;
      setRegisto(data);
      
      // Extrair hora de início e fim (com segundos para maior precisão)
      // Converter de UTC para timezone de Lisboa para exibição
      const inicio = data.inicio ? new Date(data.inicio) : null;
      const fim = data.fim ? new Date(data.fim) : null;
      
      // Formato: YYYY-MM-DDTHH:MM:SS (com segundos) no timezone de Lisboa
      const formatDateTimeLocal = (date) => {
        if (!date) return '';
        // Usar Intl para obter as partes da data no timezone configurado
        const options = { 
          timeZone: getTimezone(),
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        };
        const formatter = new Intl.DateTimeFormat('en-CA', options);
        const parts = formatter.formatToParts(date);
        const getPart = (type) => parts.find(p => p.type === type)?.value || '00';
        
        return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
      };
      
      setFormData({
        horaInicio: formatDateTimeLocal(inicio),
        horaFim: formatDateTimeLocal(fim),
        quantidade: data.quantidade_produzida?.toString() || '0',
        anomalias: data.anomalias?.toString() || '0',
        motivoEdicao: ''
      });
    } catch (error) {
      console.error('Erro ao carregar registo:', error);
      toast.error('Erro ao carregar dados do registo');
    } finally {
      setLoading(false);
    }
  };

  const validate = () => {
    const newErrors = {};
    
    if (formData.horaInicio && formData.horaFim) {
      const inicio = new Date(formData.horaInicio);
      const fim = new Date(formData.horaFim);
      if (fim < inicio) {
        newErrors.horaFim = 'Hora de fim não pode ser antes da hora de início';
      }
    }
    
    if (formData.quantidade && parseInt(formData.quantidade) < 0) {
      newErrors.quantidade = 'Quantidade não pode ser negativa';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    
    setSaving(true);
    try {
      const payload = {
        registo_id: registoId,
        motivo_edicao: formData.motivoEdicao || 'Correção pelo gestor'
      };
      
      // Função auxiliar para formatar data no timezone configurado (para comparação)
      const formatForCompare = (dateStr) => {
        if (!dateStr) return '';
        const d = new Date(dateStr);
        const options = { 
          timeZone: getTimezone(),
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false
        };
        const formatter = new Intl.DateTimeFormat('en-CA', options);
        const parts = formatter.formatToParts(d);
        const getPart = (type) => parts.find(p => p.type === type)?.value || '00';
        return `${getPart('year')}-${getPart('month')}-${getPart('day')}T${getPart('hour')}:${getPart('minute')}:${getPart('second')}`;
      };
      
      // Função para converter hora de Lisboa para UTC antes de enviar ao servidor
      // A data no input está no timezone de Lisboa, precisamos convertê-la para UTC
      const toUTC = (localDateStr) => {
        if (!localDateStr) return null;
        // Criar a data como se fosse local (o navegador interpreta como local)
        const localDate = new Date(localDateStr);
        return localDate.toISOString();
      };
      
      // Só enviar campos alterados (comparação com segundos no timezone de Lisboa)
      if (formData.horaInicio && registo?.inicio) {
        const original = formatForCompare(registo.inicio);
        if (formData.horaInicio !== original) {
          payload.hora_inicio = toUTC(formData.horaInicio);
        }
      }
      
      if (formData.horaFim && registo?.fim) {
        const original = formatForCompare(registo.fim);
        if (formData.horaFim !== original) {
          payload.hora_fim = toUTC(formData.horaFim);
        }
      }
      
      const novaQuantidade = parseInt(formData.quantidade);
      if (!isNaN(novaQuantidade) && novaQuantidade !== registo?.quantidade_produzida) {
        payload.quantidade_produzida = novaQuantidade;
      }
      
      const novasAnomalias = parseInt(formData.anomalias);
      if (!isNaN(novasAnomalias) && novasAnomalias !== (registo?.anomalias || 0)) {
        payload.anomalias = novasAnomalias;
      }
      
      await axios.put(`${API}/registos/${registoId}`, payload);
      
      toast.success('Registo atualizado com sucesso');
      onSuccess?.();
      onOpenChange(false);
    } catch (error) {
      console.error('Erro ao guardar:', error);
      toast.error(error.response?.data?.detail || 'Erro ao guardar alterações');
    } finally {
      setSaving(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds < 0) return '-';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hours > 0) {
      return `${hours}h ${mins}min ${secs}s`;
    }
    if (mins > 0) {
      return `${mins}min ${secs}s`;
    }
    return `${secs}s`;
  };

  // Calcular nova duração em tempo real
  const calcularDuracao = () => {
    if (formData.horaInicio && formData.horaFim) {
      const inicio = new Date(formData.horaInicio);
      const fim = new Date(formData.horaFim);
      const diff = (fim - inicio) / 1000;
      if (diff > 0) {
        return formatDuration(diff);
      }
    }
    return '-';
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="editar-registo-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Edit3 className="h-5 w-5 text-primary" />
            Editar Registo de Produção
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : registo ? (
          <div className="space-y-6">
            {/* Info do Registo */}
            <div className="bg-stone-50 rounded-lg p-4 space-y-2">
              <div className="flex items-center gap-2 text-sm">
                <User className="h-4 w-4 text-stone-500" />
                <span className="font-medium">{registo.operador_detalhes?.nome_completo || registo.operador_nome || 'N/A'}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Package className="h-4 w-4 text-stone-500" />
                <span>OF {registo.of_detalhes?.numero_of || registo.of_numero || 'N/A'} - {registo.operacao_nome || 'N/A'}</span>
              </div>
              {registo.lote_codigo && (
                <div className="text-xs text-stone-500">
                  Lote: {registo.lote_codigo}
                </div>
              )}
            </div>

            {/* Formulário de Edição */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="horaInicio" className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> Hora de Início
                </Label>
                <Input
                  id="horaInicio"
                  type="datetime-local"
                  step="1"
                  value={formData.horaInicio}
                  onChange={(e) => setFormData({...formData, horaInicio: e.target.value})}
                  className={errors.horaInicio ? 'border-red-500' : ''}
                  data-testid="editar-hora-inicio"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="horaFim" className="flex items-center gap-1">
                  <Clock className="h-3.5 w-3.5" /> Hora de Fim
                </Label>
                <Input
                  id="horaFim"
                  type="datetime-local"
                  step="1"
                  value={formData.horaFim}
                  onChange={(e) => setFormData({...formData, horaFim: e.target.value})}
                  className={errors.horaFim ? 'border-red-500' : ''}
                  data-testid="editar-hora-fim"
                />
                {errors.horaFim && (
                  <p className="text-xs text-red-500 flex items-center gap-1">
                    <AlertTriangle className="h-3 w-3" /> {errors.horaFim}
                  </p>
                )}
              </div>
            </div>

            {/* Duração Calculada */}
            <div className="bg-blue-50 rounded-lg p-3 text-center">
              <p className="text-xs text-blue-600 mb-1">Duração Calculada</p>
              <p className="text-xl font-bold text-blue-700">{calcularDuracao()}</p>
            </div>

            {/* Quantidade */}
            <div className="space-y-2">
              <Label htmlFor="quantidade" className="flex items-center gap-1">
                <Package className="h-3.5 w-3.5" /> Quantidade Produzida
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="quantidade"
                  type="number"
                  min="0"
                  value={formData.quantidade}
                  onChange={(e) => setFormData({...formData, quantidade: e.target.value})}
                  className={`w-32 ${errors.quantidade ? 'border-red-500' : ''}`}
                  data-testid="editar-quantidade"
                />
                <span className="text-sm text-stone-500">
                  (anterior: {registo.quantidade_produzida || 0} un.)
                </span>
              </div>
              {errors.quantidade && (
                <p className="text-xs text-red-500 flex items-center gap-1">
                  <AlertTriangle className="h-3 w-3" /> {errors.quantidade}
                </p>
              )}
            </div>

            {/* Anomalias */}
            <div className="space-y-2">
              <Label htmlFor="anomalias" className="flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" /> Anomalias
              </Label>
              <div className="flex items-center gap-3">
                <Input
                  id="anomalias"
                  type="number"
                  min="0"
                  value={formData.anomalias}
                  onChange={(e) => setFormData({...formData, anomalias: e.target.value})}
                  className="w-32"
                  data-testid="editar-anomalias"
                />
                <span className="text-sm text-stone-500">
                  (anterior: {registo.anomalias || 0} un.)
                </span>
              </div>
            </div>

            {/* Motivo da Edição */}
            <div className="space-y-2">
              <Label htmlFor="motivo">Motivo da Correção (opcional)</Label>
              <Textarea
                id="motivo"
                placeholder="Ex: Operador registou quantidade errada"
                value={formData.motivoEdicao}
                onChange={(e) => setFormData({...formData, motivoEdicao: e.target.value})}
                rows={2}
                data-testid="editar-motivo"
              />
            </div>

            {/* Histórico de Edições */}
            {registo.historico_edicoes && registo.historico_edicoes.length > 0 && (
              <div className="border-t pt-4">
                <p className="text-xs text-stone-500 flex items-center gap-1 mb-2">
                  <History className="h-3 w-3" /> Edições anteriores ({registo.historico_edicoes.length})
                </p>
                <div className="max-h-24 overflow-y-auto space-y-1">
                  {registo.historico_edicoes.map((edit, idx) => (
                    <div key={idx} className="text-xs bg-stone-50 p-2 rounded">
                      <span className="text-stone-400">{new Date(edit.data).toLocaleString('pt-PT')}</span>
                      {edit.motivo && <span className="ml-2">{edit.motivo}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Aviso */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">Atenção</p>
                <p className="text-xs">As alterações serão registadas no histórico e a eficiência será recalculada automaticamente.</p>
              </div>
            </div>

            {/* Botões */}
            <div className="flex justify-end gap-3 pt-2">
              <Button 
                variant="outline" 
                onClick={() => onOpenChange(false)}
                disabled={saving}
              >
                Cancelar
              </Button>
              <Button 
                onClick={handleSave}
                disabled={saving}
                data-testid="guardar-edicao-btn"
              >
                {saving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    A guardar...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Guardar Alterações
                  </>
                )}
              </Button>
            </div>
          </div>
        ) : (
          <div className="text-center py-8 text-stone-500">
            Registo não encontrado
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
